import { inject, Injectable, Signal } from "@angular/core";
import { ArticleInfos } from "../../models/article.model";
import { ArticlesInfosApiService } from "./article-infos-api.service";
import {
  ArticleInfosState,
  ArticlesInfosStoreService,
} from "./article-infos-store.service";
import { EntityFacadeService } from "../../../../shared/store/entity-store/entity-facade.service";
import {
  EntityMetadata,
  QueryParam,
} from "../../../../shared/store/store-types.model";
import { firstValueFrom } from "rxjs";
import {
  Untrack,
  UpdateStatus,
} from "../../../../shared/store/utils/store-utils.service";
import { DialogsService } from "../../../../shared/services/dialogs/dialogs.service";

@Injectable({ providedIn: "root" })
export class ArticlesInfosFacadeService extends EntityFacadeService<
  ArticleInfos,
  ArticlesInfosApiService,
  ArticleInfosState,
  EntityMetadata,
  ArticlesInfosStoreService
> {
  dialogs = inject(DialogsService);

  /** Chargement du catalogue en vol, partagé par les appels concurrents. */
  private chargementCatalogue?: Promise<void>;
  /** Un `loadAll` a RÉUSSI (≠ « le store contient quelque chose »). */
  private catalogueCharge = false;

  constructor() {
    const api = inject(ArticlesInfosApiService);
    const store = inject(ArticlesInfosStoreService);
    super(api, store);
    this.store.initStatus(ArticlesInfosFacadeService);
  }

  /**
   * Garantit que le catalogue complet des ArticleInfos a été chargé UNE fois.
   *
   * POURQUOI CETTE MÉTHODE EXISTE. Les appelants gardaient leur chargement par
   * `getAllEntities()().length === 0`, c'est-à-dire « le store est vide ». Or
   * « non vide » ne veut PAS dire « chargé » : la WebSocket `refreshInfosArticle`
   * y dépose les articles créés par les AUTRES sessions
   * (`HydrateService.hydrateOneInfoArticle` → `store.addOne`, sans condition).
   *
   * Il suffisait donc qu'un chargement initial échoue — une requête avortée,
   * 6,6 Mo sur un réseau qui décroche — et qu'un seul article arrive ensuite par
   * la WebSocket, pour que la garde devienne définitivement fausse. Le sélecteur
   * restait alors à deux ou trois articles étrangers **pour toute la vie de la
   * page**, sans rien pour le signaler : l'utilisateur cherche un article qui
   * existe, ne le voit pas, et conclut qu'il n'existe pas. Constaté en
   * production de tests le 10/08/2026 (realm#74), reproduit en 28 s.
   *
   * Ce que cette méthode garantit, et que la garde précédente ne pouvait pas :
   * - un seul chargement même si dix sélecteurs s'ouvrent d'un coup (les appels
   *   concurrents partagent la promesse en vol) ;
   * - un échec ne condamne pas la session — le prochain appelant retentera ;
   * - un store peuplé par la WebSocket seule ne fait plus passer le catalogue
   *   pour chargé.
   *
   * Ne rejette jamais : les appelants l'invoquent sans `await` depuis un
   * constructeur, et une promesse rejetée y deviendrait une « unhandled
   * rejection ». L'utilisateur est déjà averti par `error.interceptor`.
   */
  async ensureLoaded(): Promise<void> {
    if (this.catalogueCharge) return;
    if (this.chargementCatalogue) return this.chargementCatalogue;
    this.chargementCatalogue = this.loadAll()
      .catch(() => {
        // Volontairement muet : l'affichage revient à error.interceptor. Le seul
        // effet qui compte est que `loadAll` n'a PAS posé son drapeau — donc le
        // prochain appelant retentera.
      })
      .finally(() => {
        this.chargementCatalogue = undefined;
      });
    return this.chargementCatalogue;
  }

  /**
   * Surcharge : mémorise qu'un chargement complet a RÉUSSI.
   *
   * C'est ici que le drapeau se pose, et pas dans `ensureLoaded`, parce que le
   * catalogue est déjà chargé AILLEURS : au login, `AuthService.loadInitData`
   * appelle `HydrateService.hydrateAll()` → `loadAll()`. Si `ensureLoaded` tenait
   * son propre compte, la première ouverture d'un `vl-article-select` retirerait
   * 6,6 Mo une seconde fois pour rien.
   *
   * Le chargement forcé reste possible et reste utile : le tuto appelle
   * `hydrateAll()` à chaque changement de progression pour resynchroniser les
   * données de démo. `loadAll` recharge donc toujours quand on l'appelle
   * directement — il se contente de noter son succès au passage.
   *
   * Un échec laisse le drapeau à false (l'exception remonte avant), ce qui est
   * exactement ce qu'on veut : c'est le trou par lequel realm#74 est passé, un
   * `hydrateAll` avortant au boot dont `.catch(() => {})` avalait l'échec sans
   * que rien ne relance jamais.
   */
  override async loadAll(query?: QueryParam): Promise<void> {
    await super.loadAll(query);
    this.catalogueCharge = true;
  }

  async updateNotCurrent(articleInfos: ArticleInfos) {
    const saved = await firstValueFrom(this.api.update(articleInfos));
    return saved;
  }

  getByArticleId(articleId: string): Signal<ArticleInfos> {
    return this.store.getByArticleId(articleId);
  }

  getByCode(code: string): Signal<ArticleInfos> {
    return this.store.getByCode(code);
  }

  getByEAN14(ean14: string): Signal<ArticleInfos> {
    return this.store.getByEAN14(ean14);
  }

  @Untrack
  @UpdateStatus
  async getEan13Dispo() {
    const ean13 = await this.api.getEan13Dispo();
    return ean13;
  }

  onStats(articleId: string) {
    // Import dynamique OBLIGATOIRE : ce facade est tiré dans le bundle
    // initial (via vl-article-card / SharedModule). Un import statique du
    // dialog stats aspirerait tout l'arbre widgets dashboard + chart.js +
    // exceljs (~2 MB) dans main.js.
    import(
      "../../containers/stats-article-dialog/stats-article-dialog.component"
    ).then((m) =>
      this.dialogs.open(m.StatsArticleDialogComponent, "95dvw", "95dvh", {
        articleId,
      }),
    );
  }

  @Untrack
  @UpdateStatus
  async loadByEan13(ean13: string) {
    return await firstValueFrom(this.api.loadByEan13(ean13));
  }

  @Untrack
  @UpdateStatus
  async loadByEan14(ean14: string) {
    return await firstValueFrom(this.api.loadByEan14(ean14));
  }

  @Untrack
  @UpdateStatus
  async loadByCode(code: string) {
    return await firstValueFrom(this.api.loadByCode(code));
  }

  @Untrack
  @UpdateStatus
  async loadByArticleId(articleId: string) {
    const res = await firstValueFrom(this.api.loadByArticleId(articleId));
    if (res) {
      this.store.upsertOne({ ...res, articleId });
    }
    return res;
  }

  @Untrack
  @UpdateStatus
  async loadByNumeroLot(numero: string) {
    return await firstValueFrom(this.api.loadByNumeroLot(numero));
  }

  @Untrack
  @UpdateStatus
  async updateCurrentArticleInfos(
    articleId: string,
    ean13: string,
    ean14: string,
  ) {
    const res = await firstValueFrom(
      this.api.updateCurrentArticleInfos(articleId, ean13, ean14),
    );

    this.store.upsertOne({ ...res, articleId });
    return { ...res, articleId };
  }
}
